// Iter 55 — Public AI Sales Assistant floating chat widget.
// Mounted ONLY on the landing page (/, /landing) so it never appears
// inside the authenticated kiosk app. Glassmorphic frame, pulsing
// trigger bubble, typing dot animation, auto-scroll on new message,
// Enter-to-send. Speaks Greek by default.
import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const STORAGE_KEY = "kf_sales_chat_session";

export default function SalesChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [greeting, setGreeting] = useState("");
  const sessionRef = useRef(localStorage.getItem(STORAGE_KEY) || "");
  const endRef = useRef(null);

  // First-open: load status + show greeting
  useEffect(() => {
    api.get("/public/sales-chat/status")
      .then(({ data }) => {
        setConfigured(!!data.configured);
        setGreeting(data.greeting_gr || "");
      })
      .catch(() => setConfigured(false));
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (open && endRef.current) {
      endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages.length, open, sending]);

  // Seed greeting once the panel opens for the first time
  useEffect(() => {
    if (open && messages.length === 0 && greeting) {
      setMessages([{ role: "assistant", text: greeting }]);
    }
  }, [open, greeting, messages.length]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    const optimistic = [...messages, { role: "user", text }];
    setMessages(optimistic);
    setInput("");
    try {
      const { data } = await api.post("/public/sales-chat", {
        message: text,
        session_id: sessionRef.current || undefined,
      });
      sessionRef.current = data.session_id;
      try { localStorage.setItem(STORAGE_KEY, data.session_id); } catch { /* tolerate */ }
      setMessages((prev) => [...prev, { role: "assistant", text: data.reply }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Δοκίμασε ξανά σε λίγο.";
      setMessages((prev) => [...prev, { role: "assistant", text: `⚠️ ${msg}` }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Trigger bubble (bottom-right, offset above the global Made-by-John tag at bottom: 16px) */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="sales-chat-toggle"
        aria-label={open ? "Close AI assistant" : "Open AI assistant"}
        style={{ position: "fixed", bottom: 80, right: 16, zIndex: 9998 }}
        className={`group flex items-center gap-2 rounded-full pl-4 pr-5 py-3 font-bold text-sm shadow-2xl transition-all ${
          open
            ? "bg-slate-900 text-white hover:bg-slate-700"
            : "bg-gradient-to-br from-amber-400 to-amber-500 text-slate-950 hover:scale-105"
        }`}
      >
        {/* pulsing halo */}
        {!open && (
          <span className="absolute inset-0 rounded-full bg-amber-400 opacity-50 animate-ping pointer-events-none" />
        )}
        <span className="relative inline-flex items-center gap-2">
          {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
          <span className="hidden sm:inline">{open ? "Κλείσιμο" : "💬 AI Assistant"}</span>
        </span>
      </button>

      {/* Chat window */}
      {open && (
        <div
          data-testid="sales-chat-window"
          style={{ position: "fixed", bottom: 140, right: 16, zIndex: 9997, width: "min(380px, calc(100vw - 32px))", maxHeight: "min(560px, calc(100vh - 200px))" }}
          className="flex flex-col rounded-2xl border border-white/20 bg-slate-900/95 backdrop-blur-xl shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-white/10 bg-gradient-to-r from-amber-500/20 to-emerald-500/10 flex items-center gap-2">
            <div className="h-9 w-9 rounded-full bg-amber-400 text-slate-950 flex items-center justify-center shadow">
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="flex-1 leading-tight">
              <p className="text-white font-black text-sm tracking-tight">KioskForce AI Sales</p>
              <p className="text-[10px] uppercase tracking-widest flex items-center gap-1" data-testid="sales-chat-status">
                <span className="relative inline-flex h-2 w-2">
                  <span className={`absolute inline-flex h-full w-full rounded-full opacity-80 animate-ping ${configured ? "bg-emerald-400" : "bg-amber-400"}`} />
                  <span className={`relative inline-flex rounded-full h-2 w-2 ${configured ? "bg-emerald-500 shadow-lg shadow-emerald-500/50" : "bg-amber-500"}`} />
                </span>
                {configured ? (
                  <span className="text-emerald-300 font-black">🟢 ONLINE · GREEK / ENGLISH</span>
                ) : (
                  <span className="text-amber-300" title="EMERGENT_LLM_KEY missing in backend .env">
                    Setup needed · paste EMERGENT_LLM_KEY
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Message stream */}
          <div
            data-testid="sales-chat-messages"
            className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-slate-950/30"
            style={{ minHeight: 280 }}
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"} animate-[fadeInUp_0.3s_ease-out]`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-snug shadow ${
                    m.role === "user"
                      ? "bg-amber-400 text-slate-950 font-semibold"
                      : "bg-slate-800 text-slate-100 border border-white/10"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="sales-chat-typing">
                <div className="bg-slate-800 border border-white/10 rounded-2xl px-4 py-2 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Input bar */}
          <div className="border-t border-white/10 bg-slate-900 px-3 py-2 flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              maxLength={800}
              disabled={!configured || sending}
              placeholder={configured ? "" : "Bot is in setup mode — paste EMERGENT_LLM_KEY in backend .env"}
              data-testid="sales-chat-input"
              className="flex-1 bg-slate-800 text-white rounded-lg px-3 py-2 text-sm outline-none border border-white/10 focus:border-amber-400 disabled:opacity-50"
            />
            <button
              onClick={send}
              disabled={!configured || sending || !input.trim()}
              data-testid="sales-chat-send"
              className="h-9 w-9 rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-950 flex items-center justify-center disabled:opacity-50"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
          <p className="text-[9px] text-center text-slate-500 py-1 bg-slate-950/50">
            Powered by KioskForce AI · 60 ημέρες ΔΩΡΕΑΝ trial
          </p>
        </div>
      )}

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
